const API_URL = 'https://myblrc.in/api/api.php';
const LOCAL_URL = '/api/api.php';

export class WhatsAppApiError extends Error {
  constructor(message, fields) {
    super(message);
    this.name = 'WhatsAppApiError';
    this.fields = fields || null;
  }
}

const GROUP_PATTERN = /^https:\/\/chat\.whatsapp\.com\/(?:invite\/)?[A-Za-z0-9]{10,128}\/?(?:\?[A-Za-z0-9_=&%.-]{0,200})?$/;
const CHANNEL_PATTERN = /^https:\/\/(?:www\.)?whatsapp\.com\/channel\/[A-Za-z0-9]{8,128}\/?(?:\?[A-Za-z0-9_=&%.-]{0,200})?$/;

export function whatsAppFieldError(kind, value) {
  const link = String(value || '').trim();
  if (!link) return '';
  const pattern = kind === 'channel' ? CHANNEL_PATTERN : GROUP_PATTERN;
  if (pattern.test(link)) return '';
  return kind === 'channel'
    ? 'Enter a WhatsApp channel link, such as https://whatsapp.com/channel/…'
    : 'Enter a WhatsApp group invite link, such as https://chat.whatsapp.com/…';
}

function emptyLinks() {
  return { group_url: '', channel_url: '' };
}

function linksFrom(data) {
  return {
    group_url: String(data?.group_url || '').trim(),
    channel_url: String(data?.channel_url || '').trim(),
  };
}

async function readPayload(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

export async function fetchWhatsAppLinks() {
  const base = process.env.NODE_ENV === 'development' ? LOCAL_URL : API_URL;
  try {
    const response = await fetch(`${base}?xAction=getWhatsAppLinks&_t=${Date.now()}`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      cache: 'no-store',
    });
    const payload = await readPayload(response);
    const data = payload?.data;
    if (!response.ok || !data || data.status === 'error') return emptyLinks();
    return linksFrom(data);
  } catch (error) {
    return emptyLinks();
  }
}

export async function saveWhatsAppLinks(links) {
  const body = {
    group_url: String(links.group_url || '').trim(),
    channel_url: String(links.channel_url || '').trim(),
  };
  const fields = {
    group_url: whatsAppFieldError('group', body.group_url),
    channel_url: whatsAppFieldError('channel', body.channel_url),
  };
  const fieldErrors = Object.fromEntries(Object.entries(fields).filter(([, message]) => message));
  if (Object.keys(fieldErrors).length) {
    throw new WhatsAppApiError('Please enter valid WhatsApp links.', fieldErrors);
  }

  const token = localStorage.getItem('adminToken') || '';
  const base = process.env.NODE_ENV === 'development' ? LOCAL_URL : API_URL;
  const params = new URLSearchParams({ xAction: 'saveWhatsAppLinks', _t: Date.now().toString() });
  if (token) params.set('token', token);

  let response;
  let payload;
  try {
    response = await fetch(`${base}?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({ ...body, token }),
    });
    payload = await readPayload(response);
  } catch (error) {
    throw new WhatsAppApiError('Unable to save WhatsApp links.');
  }

  const data = payload?.data;
  if (!response.ok || !data || data.status === 'error' || payload?.status === 'error') {
    let message = data?.message || payload?.message || 'Unable to save WhatsApp links.';
    if (/invalid action/i.test(message)) {
      message = 'The settings API is not deployed yet. Upload the api folder so myblrc.in serves the updated api.php.';
    }
    throw new WhatsAppApiError(message, data?.fields || null);
  }
  return { ...linksFrom(data), message: data.message || 'WhatsApp links saved.' };
}

export async function loadWhatsAppLinksForAdmin() {
  const base = process.env.NODE_ENV === 'development' ? LOCAL_URL : API_URL;
  const token = localStorage.getItem('adminToken') || '';
  const params = new URLSearchParams({ xAction: 'getWhatsAppLinks', _t: Date.now().toString() });
  if (token) params.set('token', token);
  let response;
  let payload;
  try {
    response = await fetch(`${base}?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      cache: 'no-store',
    });
    payload = await readPayload(response);
  } catch (error) {
    throw new WhatsAppApiError('Unable to load WhatsApp links.');
  }
  const data = payload?.data;
  if (!response.ok || !data || data.status === 'error') {
    throw new WhatsAppApiError(data?.message || 'Unable to load WhatsApp links.');
  }
  return linksFrom(data);
}
